// <s-video-backdrop> plays its muted videos in sequence with a crossfade, loops the set, and honors pause + reduced motion.
class SVideoBackdrop extends HTMLElement {
  #ac = null;
  #index = 0;
  #userPaused = false;

  connectedCallback() {
    this.videos = [...this.querySelectorAll('video')];
    this.videos.forEach((v, i) => { v.setAttribute('aria-hidden', 'true'); v.toggleAttribute('data-inactive', i !== 0); });
    this.toggle = this.querySelector('[data-backdrop-toggle]');
    if (!this.videos.length) {
      this.toggle?.setAttribute('hidden', '');
      return;
    }

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    if (this.videos.length === 1) this.videos[0].loop = true;
    this.videos.forEach((video, i) => {
      video.addEventListener('ended', () => this.#advance(i), opts);
    });

    this.addEventListener('command', (e) => {
      if (e.command === '--toggle') this.#setPaused(!this.#userPaused);
    }, opts);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#current().pause();
      else if (!this.#userPaused) this.#play();
    }, opts);

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) this.#setPaused(true);
    else this.#play();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #current() {
    return this.videos[this.#index];
  }

  #play() {
    const p = this.#current().play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }

  #advance(fromIndex) {
    if (fromIndex !== this.#index || this.videos.length < 2) return;
    const outgoing = this.#current();
    this.#index = (this.#index + 1) % this.videos.length;
    const incoming = this.#current();
    incoming.currentTime = 0;
    incoming.toggleAttribute('data-inactive', false);
    outgoing.toggleAttribute('data-inactive', true);
    if (!this.#userPaused) this.#play();
  }

  #setPaused(paused) {
    this.#userPaused = paused;
    if (paused) this.#current().pause();
    else this.#play();
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-pressed', String(paused));
    this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
    this.toggle.querySelector('[data-icon-pause]')?.classList.toggle('hidden', paused);
    this.toggle.querySelector('[data-icon-play]')?.classList.toggle('hidden', !paused);
  }
}

if (!customElements.get('s-video-backdrop')) customElements.define('s-video-backdrop', SVideoBackdrop);
